import React, { memo, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import DataTable from '@common/components/datatable';
import type { DataTableColumn } from '@common/components/datatable/types';
import ImageItem from '@common/components/image-item/large';
import ExpirationWall from '@common/components/expiration-wall';
import SearchBar from '@common/components/search-bar';
import Spinner from '@common/components/spinner';
import Alert from '@common/components/alert';
import { AlertService } from '@common/services/alert';
import { useRequest } from '@common/services/api';
import { useThunkDispatch } from '@hooks/redux';
import type { FailedRequest } from '@common/services/api/api';
import type { Organisation, OrganisationScope } from '@common/types/objects';
import ErrorComponent from '../../components/error';
import { NetworksSelectError } from '../../../organisation/actions';
import type { Props } from './types';

type OrganisationListItem = OrganisationScope & Pick<Organisation, 'trial'>;

interface OrganisationsResponse {
  data: OrganisationListItem[];
  meta: Record<string, unknown>;
}

const OrganisationRow = memo(({ item }: { item: OrganisationListItem }) => (
  <ImageItem
    className="ImageItem--clickable"
    imageSize={55}
    infoIcon="public"
    image={item.brand_icon ?? undefined}
    name={item.name}
  />
));

function OrganisationsContainer({
  selectOrganisation,
  logout,
  history,
}: Props) {
  const [selecting, setSelecting] = useState(false);
  const [search, setSearch] = useState('');
  const [showExpirationWall, setShowExpirationWall] = useState(false);
  const dispatch = useThunkDispatch();
  const { t } = useTranslation();

  const onSuccess = useCallback((result: OrganisationsResponse) => {
    dispatch({
      type: 'auth/RECEIVE_ORGANISATIONS',
      items: result.data,
    });
  }, [dispatch]);

  const {
    data,
    loading,
    error,
    reset,
  } = useRequest<OrganisationsResponse>('/v1/organisations', {
    onSuccess,
  });

  const selectWrapper = useCallback((fn: (id: string) => Promise<string | undefined>) => async (id: string) => {
    try {
      setSelecting(true);
      const path = await fn(id);
      if (path) history.push(path);
    } catch (selectError) {
      setSelecting(false);
      if (selectError instanceof NetworksSelectError) {
        AlertService.error(t('authentication:networks_select_error'));
      } else {
        AlertService.error(t('common:something_went_wrong'));
        throw selectError;
      }
    }
  }, [history, t]);

  const handleSelectOrganisation = useMemo(
    () => selectWrapper(selectOrganisation),
    [selectOrganisation, selectWrapper],
  );

  const handleRowClick = useCallback((row: Record<string, unknown>) => {
    const item = row as OrganisationListItem;

    if (item.trial && new Date(item.trial.expires_at) < new Date()) {
      setShowExpirationWall(true);
      return;
    }

    handleSelectOrganisation(item.id);
  }, [handleSelectOrganisation]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } finally {
      history.push('/auth/login');
    }
  }, [history, logout]);

  const filteredData = useMemo(() => {
    if (!search) return data;

    const query = search.toLowerCase();
    return data.filter((organisation) => organisation.name.toLowerCase().includes(query));
  }, [data, search]);

  const columns = useMemo<DataTableColumn[]>(() => [
    {
      key: 'organisation',
      label: '',
      className: '!tw-border-0 !tw-py-0 !tw-px-0 first:!tw-pl-0 last:!tw-pr-0',
      options: {
        size: '100%',
        render: (row: OrganisationListItem) => <OrganisationRow item={row} />,
      },
    },
  ], []);

  const emptyView = useMemo(() => (
    search ? (
      <Trans i18nKey="authentication:no_networks_found" values={{ search }} />
    ) : (
      <Alert type="warning">
        <Trans
          i18nKey="authentication:no_organisations_available"
          components={[<a key="logout" onClick={handleLogout} role="button" />]}
        />
      </Alert>
    )
  ), [handleLogout, search]);

  if (selecting) {
    return (
      <div id="Organisations" className="balloon">
        <Spinner size="large" centered />
      </div>
    );
  }

  if (error) {
    return (
      <div id="Organisations" className="balloon">
        <h2><Trans i18nKey="authentication:organisations_select_header" /></h2>
        <ErrorComponent
          error={error as unknown as FailedRequest}
          onRetry={() => reset()}
          logout={handleLogout}
        />
      </div>
    );
  }

  return (
    <div id="Organisations" className="balloon">
      {showExpirationWall && (
        <ExpirationWall onSwitch={() => setShowExpirationWall(false)} />
      )}
      <h2><Trans i18nKey="authentication:organisations_select_header" /></h2>
      <SearchBar onSearch={(value) => setSearch(value ?? '')} debounce={false} filter={false} />
      <DataTable
        className="OrganisationsList"
        columns={columns}
        data={filteredData}
        loading={loading}
        showHeader={false}
        onRowClick={handleRowClick}
        emptyView={<div className="OrganisationsList__empty">{emptyView}</div>}
      />
    </div>
  );
}

const mapDispatchToProps = {
  selectOrganisation: require('../../../organisation/actions').selectOrganisation,
  logout: require('../../actions/logout').default,
};

export default connect(undefined, mapDispatchToProps)(memo(OrganisationsContainer));
